"use client";

import { resourceNavigation } from "@/components/resource-navigation";
import { taxStatusColors } from "@/components/tax-status-badge";
import { useStaffTaxApplications } from "@/hooks/spacetime/use-staff-tax-applications";
import {
  Building,
  Building2,
  ChartColumn,
  CodeXml,
  Coins,
  Hourglass,
  CreditCard,
  Home,
  Key,
  Mail,
  MonitorDown,
  Palette,
  SlidersHorizontal,
  ScrollText,
  Settings,
  TriangleAlert,
  User,
  Users,
} from "lucide-react";
import {
  LIBUM_DEV_MAIL_URL,
  LIBUM_DISCORD_URL,
  LIBUM_DOCS_GLACI_URL,
  LIBUM_DOCS_POWERON_PRO_URL,
  LIBUM_POWERON_PRO_EULA_URL,
  LIBUM_POWERON_PRO_INSTALL_URL,
} from "@/lib/constants";

import Discord from "@/components/icons/discord";
import Glaci from "@/components/icons/glaci";
import PowerOnPro from "@/components/icons/poweron-pro";
import SparkleIcon from "@/components/icons/sparkle-icon";
import {
  useCurrentUser,
  useCurrentOrganization,
  useMembers,
  useMyMemberships,
  usePendingOrgs,
  useSubscriptions,
  useTaxExemption,
} from "@/hooks/spacetime";
import { isOrgAdminOrOwner } from "@/lib/org-roles";
import { useStaffNavigationAccess } from "@/providers/staff-access";

export function useAppNavigation() {
  const { user } = useCurrentUser();
  const { organization } = useCurrentOrganization();
  const { pendingOrgs } = usePendingOrgs();
  const { subscriptions } = useSubscriptions(organization);
  const { memberships } = useMyMemberships();
  const { members } = useMembers(organization?.id);
  const { taxExemption, isReady: taxReady } = useTaxExemption(organization?.id);
  const taxStatusBadge =
    taxReady && organization && taxExemption?.organizationId === organization.id
      ? {
          submitted: {
            label: "Pending",
            className: taxStatusColors["submitted"],
          },
          "in review": {
            label: "In review",
            className: taxStatusColors["in review"],
          },
          approved: {
            label: "Exempt",
            className: taxStatusColors["approved"],
          },
          rejected: {
            label: "Rejected",
            className: taxStatusColors["rejected"],
          },
        }[taxExemption.status]
      : undefined;
  const { attentionCount: taxAttentionCount } = useStaffTaxApplications();
  const canReviewTax = useStaffNavigationAccess(user?.id);
  const hasActiveOrganization = !!organization;
  const hasPendingOnlyOrganization =
    pendingOrgs.length > 0 && !hasActiveOrganization;
  const shouldShowOrganizationSettings =
    hasActiveOrganization || !hasPendingOnlyOrganization;
  // Open join requests surface as a count badge on the Members tab, for
  // viewers who can actually approve them.
  const joinRequestCount =
    user && isOrgAdminOrOwner(organization, user, memberships)
      ? members.filter((member) => member.status === "requested").length
      : 0;

  const data = {
    user: {
      id: user?.id,
      name: user?.name,
      email: user?.email,
      avatar: user?.avatar,
    },
    navMain: [
      {
        title: "PowerOn Pro",
        url: "/poweron-pro",
        icon: PowerOnPro,
        groups: [
          {
            title: "General",
            pages: [
              {
                title: "Users",
                url: "/poweron-pro/users",
                icon: Users,
              },
            ],
          },
          {
            title: "Resources",
            className: "mt-auto",
            pages: [
              {
                title: "Read the EULA",
                url: LIBUM_POWERON_PRO_EULA_URL,
                icon: ScrollText,
              },
              {
                title: "Install in Visual Studio Code",
                url: LIBUM_POWERON_PRO_INSTALL_URL,
                icon: MonitorDown,
              },
              {
                title: "Visit Developer Docs",
                url: LIBUM_DOCS_POWERON_PRO_URL,
                icon: CodeXml,
              },
            ],
          },
        ],
      },
      {
        title: "Glaci",
        url: "/glaci",
        icon: Glaci,
        groups: [
          {
            title: "General",
            pages: [
              {
                title: "Overview",
                url: "/glaci/overview",
                icon: Home,
              },
              {
                title: "Personal",
                url: "/glaci/personal",
                icon: User,
              },
            ],
          },
          {
            title: "Organization",
            pages: [
              {
                title: "Branding",
                url: "/glaci/branding",
                icon: Palette,
              },
              {
                title: "Configuration",
                url: "/glaci/configuration",
                icon: SlidersHorizontal,
              },
              {
                title: "Insights",
                url: "/glaci/insights",
                icon: ChartColumn,
              },
            ],
          },
          {
            title: "Resources",
            className: "mt-auto",
            pages: [
              {
                title: "Download Glaci",
                url: "", // TODO: Add download URL
                icon: MonitorDown,
              },
              {
                title: "Visit Developer Docs",
                url: LIBUM_DOCS_GLACI_URL,
                icon: CodeXml,
              },
            ],
          },
        ],
      },
    ],
    navResources: resourceNavigation,
    navSecondary: [
      {
        title: "Upgrade to Plus",
        url: "/settings/subscriptions",
        icon: SparkleIcon,
        className: "upgrade-nav",
      },
      {
        title: "Insights",
        url: "/insights",
        icon: ChartColumn,
        visible: canReviewTax,
        animatePresence: true,
        groups: canReviewTax
          ? [
              {
                title: "General",
                pages: [
                  {
                    title: "Overview",
                    url: "/insights",
                    exactMatch: true,
                    icon: ChartColumn,
                  },
                ],
              },
              {
                title: "Products",
                pages: [
                  {
                    title: "PowerOn Pro",
                    url: "/insights/poweron-pro",
                    exactMatch: true,
                    icon: PowerOnPro,
                    iconClassName: "scale-125",
                  },
                ],
              },
            ]
          : [],
      },
      {
        title: "Settings",
        url: "/settings",
        icon: Settings,
        className: "settings-nav",
        showDot:
          joinRequestCount > 0 || (canReviewTax && taxAttentionCount > 0),
        groups: [
          {
            title: "Personal",
            pages: [
              {
                title: "Your Profile",
                url: "/settings/profile",
                icon: User,
              },
              {
                title: "Organizations",
                url: "/settings/organizations",
                icon: Building2,
              },
            ],
          },
          {
            title: "Organization",
            pages: shouldShowOrganizationSettings
              ? [
                  {
                    title: "General",
                    url: "/settings/general",
                    icon: Building,
                  },
                  {
                    title: "API Keys",
                    url: "/settings/api-keys",
                    icon: Key,
                  },
                  {
                    title: "Sales Tax",
                    statusBadge: taxStatusBadge,
                    url: "/settings/sales-tax",
                    icon: Coins,
                  },
                  {
                    title: "Members",
                    url: "/settings/members",
                    icon: Users,
                    badge: joinRequestCount > 0 ? joinRequestCount : undefined,
                  },
                  {
                    title: "Subscriptions",
                    url: "/settings/subscriptions",
                    icon: CreditCard,
                  },
                  {
                    title: "Danger Zone",
                    url: "/settings/danger-zone",
                    icon: TriangleAlert,
                  },
                ]
              : [],
          },
          ...(canReviewTax
            ? [
                {
                  title: "Administration",
                  pages: [
                    {
                      title: "Grace Periods",
                      url: "/settings/grace-periods",
                      icon: Hourglass,
                    },
                    {
                      title: "Tax Applications",
                      badge: taxAttentionCount || undefined,
                      url: "/settings/tax-applications",
                      icon: ScrollText,
                    },
                  ],
                },
              ]
            : []),
          {
            title: "Resources",
            className: "mt-auto",
            pages: [
              {
                title: "Join the Libum Community",
                url: LIBUM_DISCORD_URL,
                icon: Discord,
              },
              {
                title: "Send us a Message",
                url: LIBUM_DEV_MAIL_URL,
                icon: Mail,
              },
            ],
          },
        ],
      },
    ],
  };

  return {
    data,
    organization,
    subscriptions,
    canReviewTax,
    hasPendingOnlyOrganization,
  };
}
